/**
 * LpWorkbenchShell - navy top bar matching production-details.html
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { getLoginPath, getOrgSlatePath, getOrgUploadPath } from '../../utils/postLoginNavigation';
import { getLpName } from '../../utils/orgLpHelpers';
import { orgAuthApi } from '../../services/api';
import LanguageSwitch from '../LanguageSwitch';
import '../../styles/LpWorkbench.css';

function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'LP';
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

export default function LpWorkbenchShell({
  children,
  showAddProduction = false,
  slateTheme = false,
  backToSlate = false,
  bare = false,
  /** Hide language switch (production detail pages match reference). */
  hideLanguage = false,
  /** Name only in header — omit Line Producer subtitle. */
  compactProfile = false,
  /** Productions list chrome — taller portfolio header (72px). */
  portfolioChrome = false,
  /** When set, profile menu shows Delete production for this id */
  deleteProductionId = null,
  deleteProductionName = '',
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const name = getLpName();
  const initials = initialsFromName(name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const menuRef = useRef(null);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // LP reference UI is English; pages without a language switch stay in English.
  useEffect(() => {
    if (slateTheme && hideLanguage && i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [slateTheme, hideLanguage, i18n]);

  function handleLogout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    closeMenu();
    navigate(getLoginPath(), { replace: true });
  }

  function openDeleteConfirm() {
    if (!deleteProductionId || deleting) return;
    setDeleteError('');
    closeMenu();
    setConfirmOpen(true);
  }

  function closeDeleteConfirm() {
    if (deleting) return;
    setConfirmOpen(false);
    setDeleteError('');
  }

  async function confirmDeleteProduction() {
    if (!deleteProductionId || deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await orgAuthApi.deleteProduction(deleteProductionId);
      setConfirmOpen(false);
      navigate(getOrgSlatePath(), { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setDeleteError(
        typeof detail === 'string' ? detail : t('lpWorkbench.deleteProductionFailed'),
      );
    } finally {
      setDeleting(false);
    }
  }

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu();
    }
    function onKey(e) {
      if (e.key === 'Escape') closeMenu();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (!confirmOpen) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') closeDeleteConfirm();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmOpen, deleting]);

  const productionLabel = deleteProductionName || t('lpWorkbench.thisProduction');

  return (
    <div className={`lp-root${slateTheme ? ' lp-slate-theme' : ''}${portfolioChrome ? ' lp-slate-portfolio' : ''}`}>
      <div className="lp-top">
        <Link to={getOrgSlatePath()} className="lp-bt">
          {slateTheme ? (
            <>
              <span className="lp-brand-mark" aria-hidden="true">GM</span>
              <span className="lp-brand-text">{t('lpWorkbench.brandTitle')}</span>
            </>
          ) : (
            t('lpWorkbench.brand')
          )}
        </Link>
        {backToSlate ? (
          <Link to={getOrgSlatePath()} className="lp-back">{t('lpWorkbench.slate')}</Link>
        ) : null}
        {showAddProduction && !bare ? (
          <Link
            to={getOrgUploadPath()}
            className="lp-back"
            style={{ borderLeft: 'none', paddingLeft: 0, marginLeft: 8 }}
          >
            {t('lpWorkbench.addProduction')}
          </Link>
        ) : null}
        <div className="lp-top-right">
          {!hideLanguage ? <LanguageSwitch enEsOnly className="lang-switch--lp" /> : null}
          <div className="lp-profile-wrap" ref={menuRef}>
            <button
              type="button"
              className={`lp-profile${compactProfile ? ' lp-profile-compact' : ''}`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              title={name || t('lpWorkbench.account')}
            >
              <span className="lp-pav">{initials}</span>
              {slateTheme && name ? (
                <span className="lp-profile-copy">
                  <span className="lp-profile-name">{name}</span>
                  {!compactProfile ? (
                    <span className="lp-profile-role">{t('lpWorkbench.lineProducerRole')}</span>
                  ) : null}
                </span>
              ) : null}
              <span className="lp-pcaret">{slateTheme ? '⌄' : '▾'}</span>
            </button>
            {menuOpen ? (
              <div className="lp-profile-menu" role="menu">
                {name ? (
                  <div className="lp-profile-menu-head">
                    <strong>{name}</strong>
                    <small>{t('lpWorkbench.lineProducerRole')}</small>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="lp-profile-menu-item"
                  role="menuitem"
                  onClick={() => {
                    closeMenu();
                    navigate('/org/admin');
                  }}
                >
                  <span className="lp-profile-menu-ico" aria-hidden="true">◇</span>
                  {t('lpWorkbench.demoTools')}
                </button>
                <button
                  type="button"
                  className="lp-profile-menu-item"
                  role="menuitem"
                  onClick={() => {
                    closeMenu();
                    navigate('/profile');
                  }}
                >
                  <span className="lp-profile-menu-ico" aria-hidden="true">♙</span>
                  {t('lpWorkbench.profile')}
                </button>
                {deleteProductionId ? (
                  <button
                    type="button"
                    className="lp-profile-menu-item lp-profile-menu-item-danger"
                    role="menuitem"
                    disabled={deleting}
                    onClick={openDeleteConfirm}
                  >
                    <span className="lp-profile-menu-ico" aria-hidden="true">×</span>
                    {t('lpWorkbench.deleteProduction')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="lp-profile-menu-item"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  <span className="lp-profile-menu-ico" aria-hidden="true">↪</span>
                  {t('lpWorkbench.signOut')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {children}

      {confirmOpen ? (
        <div
          className="lp-modal-wrap lp-modal-wrap-top"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-delete-prod-title"
          onClick={closeDeleteConfirm}
        >
          <div className="lp-modal lp-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="lp-modal-h">
              <span id="lp-delete-prod-title">{t('lpWorkbench.deleteProductionTitle')}</span>
              <button
                type="button"
                className="x"
                onClick={closeDeleteConfirm}
                disabled={deleting}
                aria-label={t('lpWorkbench.close')}
              >
                &times;
              </button>
            </div>
            <div className="lp-modal-sub">
              <Trans
                i18nKey="lpWorkbench.deleteProductionBody"
                values={{ name: productionLabel }}
                components={{ b: <b /> }}
              />
            </div>
            {deleteError ? (
              <div className="lp-delete-err" role="alert">{deleteError}</div>
            ) : null}
            <div className="lp-modal-actions">
              <button
                type="button"
                className="lp-bn-btn ghost"
                onClick={closeDeleteConfirm}
                disabled={deleting}
              >
                {t('lpWorkbench.cancel')}
              </button>
              <button
                type="button"
                className="lp-bn-btn lp-bn-btn-danger"
                onClick={confirmDeleteProduction}
                disabled={deleting}
              >
                {deleting ? t('lpWorkbench.deleting') : t('lpWorkbench.delete')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LpBreadcrumbLink({ to, children }) {
  return (
    <Link to={to} className="lp-back" style={{ border: 'none', padding: 0, color: 'var(--accent)' }}>
      {children}
    </Link>
  );
}

export function LpBreadcrumbSep() {
  return <span style={{ color: 'var(--faint)', margin: '0 6px' }}>›</span>;
}

export function LpBreadcrumbCurrent({ children }) {
  return <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{children}</span>;
}
